// La biblioteca de la paciente de ejemplo se dibuja en dos sitios (el héroe y la sección
// Biblioteca) y los dos tienen que enseñar exactamente los mismos registros. Por eso la
// geometría vive aquí una sola vez, portada de los tableros del lienzo (Biblioteca.dc.html,
// Biblioteca_Movil.dc.html, Heroe_B2.dc.html), y no dentro de ningún componente.
//
// Sin hex ni clases: el tono sale como nombre de token (los trinquetes prohíben el hex) y cada
// consumidor lo traduce a sus propias clases.

// Orden de funciones() en el tablero: también es el orden de teclado de la Biblioteca y el
// orden de los sectores (el sector k es la función k).
export const CLAVES_BIBLIOTECA = [
    'sesiones',
    'plantillas',
    'informes',
    'psicometria',
    'manuscritos',
    'plan',
    'notas',
    'carpetas',
] as const;

export type ClaveBiblioteca = (typeof CLAVES_BIBLIOTECA)[number];

// Azar con semilla fija, la misma de los tableros: el servidor y el cliente generan la misma
// biblioteca, así que la hidratación no ve diferencias.
export const SEMILLA_BIBLIOTECA = 20261006;

export interface GeometriaBiblioteca {
    readonly radios: readonly number[];
    readonly huecosPorAnillo: readonly number[];
    // Registros de cada función (sector) al acabar el acto 1 del héroe.
    readonly llenos: readonly number[];
    // Hueco en px a cada lado del sector, convertido a grados en cada anillo.
    readonly separacion: number;
    readonly desfaseRadio: number;
    readonly largo: { readonly base: number; readonly variacion: number };
    readonly grosor: { readonly base: number; readonly variacion: number };
    // Corona del hit test de funcionEn(): fuera de ella el puntero no señala ningún sector.
    readonly coronaSensible: { readonly min: number; readonly max: number };
}

export const GEOMETRIA_ESCRITORIO: GeometriaBiblioteca = {
    radios: [70, 102, 134, 166, 198],
    huecosPorAnillo: [4, 7, 10, 12, 15],
    llenos: [48, 22, 30, 34, 38, 26, 16, 44],
    separacion: 7,
    desfaseRadio: 2,
    largo: { base: 15, variacion: 8 },
    grosor: { base: 5, variacion: 2 },
    coronaSensible: { min: 58, max: 242 },
};

export const GEOMETRIA_MOVIL: GeometriaBiblioteca = {
    radios: [40, 58, 76, 94, 112],
    huecosPorAnillo: [3, 5, 7, 9, 11],
    llenos: [35, 16, 22, 25, 28, 19, 12, 32],
    separacion: 5,
    desfaseRadio: 1.5,
    largo: { base: 8, variacion: 4 },
    grosor: { base: 4, variacion: 1 },
    coronaSensible: { min: 33, max: 131 },
};

export interface AnilloGuia {
    readonly r: number;
    readonly guiones: string;
}

// El fondo de la biblioteca: sectores, arcos exteriores y anillos guía, con sus grosores, en px
// del escenario de la sección Biblioteca (Biblioteca.dc.html, Biblioteca_Movil.dc.html). El héroe
// dibuja la misma biblioteca con otro centro y traslada estos trazados, nunca los copia.
export interface TrazadoBiblioteca {
    readonly centro: { readonly x: number; readonly y: number };
    readonly sectores: readonly string[];
    readonly arcos: readonly string[];
    readonly anillos: readonly AnilloGuia[];
    readonly grosorSector: number;
    readonly grosorAnillo: number;
    readonly desfaseGuiones: number;
}

export const TRAZADO_ESCRITORIO: TrazadoBiblioteca = {
    centro: { x: 620, y: 280 },
    sectores: [
        'M396.3 269.0 A224 224 0 0 1 454.0 129.6 L567.6 243.2 A64 64 0 0 0 557.0 269.0 Z',
        'M469.6 114.0 A224 224 0 0 1 609.0 56.3 L609.0 217.0 A64 64 0 0 0 583.2 227.6 Z',
        'M631.0 56.3 A224 224 0 0 1 770.4 114.0 L656.8 227.6 A64 64 0 0 0 631.0 217.0 Z',
        'M786.0 129.6 A224 224 0 0 1 843.7 269.0 L683.0 269.0 A64 64 0 0 0 672.4 243.2 Z',
        'M843.7 291.0 A224 224 0 0 1 786.0 430.4 L672.4 316.8 A64 64 0 0 0 683.0 291.0 Z',
        'M770.4 446.0 A224 224 0 0 1 631.0 503.7 L631.0 343.0 A64 64 0 0 0 656.8 332.4 Z',
        'M609.0 503.7 A224 224 0 0 1 469.6 446.0 L583.2 332.4 A64 64 0 0 0 609.0 343.0 Z',
        'M454.0 430.4 A224 224 0 0 1 396.3 291.0 L557.0 291.0 A64 64 0 0 0 567.6 316.8 Z',
    ],
    arcos: [
        'M384.3 268.0 A236 236 0 0 1 444.9 121.8',
        'M461.8 104.9 A236 236 0 0 1 608.0 44.3',
        'M632.0 44.3 A236 236 0 0 1 778.2 104.9',
        'M795.1 121.8 A236 236 0 0 1 855.7 268.0',
        'M855.7 292.0 A236 236 0 0 1 795.1 438.2',
        'M778.2 455.1 A236 236 0 0 1 632.0 515.7',
        'M608.0 515.7 A236 236 0 0 1 461.8 455.1',
        'M444.9 438.2 A236 236 0 0 1 384.3 292.0',
    ],
    anillos: [
        { r: 70, guiones: '40.98 14' },
        { r: 102, guiones: '66.11 14' },
        { r: 134, guiones: '91.24 14' },
        { r: 166, guiones: '116.38 14' },
        { r: 198, guiones: '141.51 14' },
    ],
    grosorSector: 8,
    grosorAnillo: 1.5,
    desfaseGuiones: -7,
};

export const TRAZADO_MOVIL: TrazadoBiblioteca = {
    centro: { x: 163, y: 170 },
    sectores: [
        'M38.2 163.0 A125 125 0 0 1 69.8 86.7 L132.4 149.3 A37 37 0 0 0 126.7 163.0 Z',
        'M79.7 76.8 A125 125 0 0 1 156.0 45.2 L156.0 133.7 A37 37 0 0 0 142.3 139.4 Z',
        'M170.0 45.2 A125 125 0 0 1 246.3 76.8 L183.7 139.4 A37 37 0 0 0 170.0 133.7 Z',
        'M256.2 86.7 A125 125 0 0 1 287.8 163.0 L199.3 163.0 A37 37 0 0 0 193.6 149.3 Z',
        'M287.8 177.0 A125 125 0 0 1 256.2 253.3 L193.6 190.7 A37 37 0 0 0 199.3 177.0 Z',
        'M246.3 263.2 A125 125 0 0 1 170.0 294.8 L170.0 206.3 A37 37 0 0 0 183.7 200.6 Z',
        'M156.0 294.8 A125 125 0 0 1 79.7 263.2 L142.3 200.6 A37 37 0 0 0 156.0 206.3 Z',
        'M69.8 253.3 A125 125 0 0 1 38.2 177.0 L126.7 177.0 A37 37 0 0 0 132.4 190.7 Z',
    ],
    arcos: [
        'M29.2 162.0 A134 134 0 0 1 62.8 81.1',
        'M74.1 69.8 A134 134 0 0 1 155.0 36.2',
        'M171.0 36.2 A134 134 0 0 1 251.9 69.8',
        'M263.2 81.1 A134 134 0 0 1 296.8 162.0',
        'M296.8 178.0 A134 134 0 0 1 263.2 258.9',
        'M251.9 270.2 A134 134 0 0 1 171.0 303.8',
        'M155.0 303.8 A134 134 0 0 1 74.1 270.2',
        'M62.8 258.9 A134 134 0 0 1 29.2 178.0',
    ],
    anillos: [
        { r: 40, guiones: '21.42 10' },
        { r: 58, guiones: '35.55 10' },
        { r: 76, guiones: '49.69 10' },
        { r: 94, guiones: '63.83 10' },
        { r: 112, guiones: '77.96 10' },
    ],
    grosorSector: 6,
    grosorAnillo: 1.2,
    desfaseGuiones: -5,
};

// Mueve el punto final de un comando M, L o A (en A, las cinco primeras cifras son radios y banderas).
// Las coordenadas del lienzo llevan un decimal; los radios y las banderas se dejan tal cual.
function desplazarComando(comando: string, cifras: readonly number[], dx: number, dy: number): string {
    const ultima = cifras.length - 1;
    const texto = cifras.map((cifra, i) => {
        if (i === ultima - 1) return (cifra + dx).toFixed(1);
        if (i === ultima) return (cifra + dy).toFixed(1);
        return String(cifra);
    });
    return `${comando}${texto.join(' ')}`;
}

/** Traslada un trazado M/L/A/Z del lienzo; da la misma cadena que el tablero habría escrito en el destino. */
export function trasladarTrazado(d: string, dx: number, dy: number): string {
    const movido = d.replace(/([MLA])([-\d.\s]+)/g, (_, comando: string, resto: string) => {
        const cifras = resto.trim().split(/\s+/).map(Number);
        return `${desplazarComando(comando, cifras, dx, dy)} `;
    });
    return movido.trimEnd();
}

/**
 * El mismo fondo con otro centro. El héroe (Heroe_B2) centra la biblioteca en (406, 268) y la
 * sección en (620, 280): los trazados se calculan desde los de la sección, nunca se copian.
 */
export function trazadoCentradoEn(trazado: TrazadoBiblioteca, centro: { readonly x: number; readonly y: number }): TrazadoBiblioteca {
    const [dx, dy] = [centro.x - trazado.centro.x, centro.y - trazado.centro.y];
    if (dx === 0 && dy === 0) return trazado;
    const mover = (d: string) => trasladarTrazado(d, dx, dy);
    return { ...trazado, centro, sectores: trazado.sectores.map(mover), arcos: trazado.arcos.map(mover) };
}

export type TonoRegistro = 'surface-2' | 'tint-accent' | 'tint-accent-strong' | 'accent-blue' | 'ink';

// La lista de ocho tonos del tablero en su orden (cuatro blancos): el índice sale del azar, así
// que cambiar el orden o deduplicar cambiaría la biblioteca.
const TONOS: readonly TonoRegistro[] = [
    'surface-2',
    'surface-2',
    'surface-2',
    'surface-2',
    'tint-accent',
    'tint-accent-strong',
    'accent-blue',
    'ink',
];

export interface RegistroBiblioteca {
    readonly sector: number;
    readonly anillo: number;
    readonly indiceEnSector: number;
    // Grados, empezando en 180 (a la izquierda del centro) y en sentido horario en pantalla.
    readonly angulo: number;
    readonly radio: number;
    readonly largo: number;
    readonly grosor: number;
    readonly tono: TonoRegistro;
    // Cuarta tirada de cada hueco: el héroe la usa para escalonar la llegada de los registros.
    readonly azarLlegada: number;
    readonly cos: number;
    readonly sin: number;
    // Centro del registro relativo al centro de la biblioteca.
    readonly cx: number;
    readonly cy: number;
}

const SECTORES = 8;
const GRADOS_SECTOR = 45;

interface PosicionHueco {
    readonly sector: number;
    readonly anillo: number;
    readonly indiceEnSector: number;
    readonly angulo: number;
    readonly radio: number;
}

// Port exacto del azar de los tableros (Math.imul y desplazamientos sin signo): cualquier
// diferencia movería todos los registros.
function crearAzar(semilla: number): () => number {
    let estado = semilla;
    return () => {
        estado = (estado + 0x6d2b79f5) | 0;
        let x = Math.imul(estado ^ (estado >>> 15), 1 | estado);
        x = (x + Math.imul(x ^ (x >>> 7), 61 | x)) ^ x;
        return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
    };
}

// Redondeo fijo para que las cadenas de estilo del servidor y del cliente coincidan; `+ 0` evita
// el -0, que haría distintos dos registros iguales.
function redondear(valor: number, decimales: number): number {
    const factor = 10 ** decimales;
    return Math.round(valor * factor) / factor + 0;
}

// Coseno, seno y centro se calculan con el ángulo sin redondear, como el tablero; solo se
// redondea el resultado.
function proyectar(posicion: PosicionHueco, largo: number) {
    const radianes = (posicion.angulo * Math.PI) / 180;
    const distancia = posicion.radio + largo / 2;
    return {
        angulo: redondear(posicion.angulo, 2),
        cos: redondear(Math.cos(radianes), 4),
        sin: redondear(Math.sin(radianes), 4),
        cx: redondear(distancia * Math.cos(radianes), 2),
        cy: redondear(distancia * Math.sin(radianes), 2),
    };
}

// El tablero tira el azar en este orden para TODOS los huecos, llenos o no: largo, grosor, tono
// y una cuarta tirada. Saltarse un hueco vacío desplazaría el azar de los siguientes.
function crearRegistro(geometria: GeometriaBiblioteca, azar: () => number, posicion: PosicionHueco): RegistroBiblioteca {
    const largo = geometria.largo.base + Math.round(azar() * geometria.largo.variacion);
    const grosor = geometria.grosor.base + Math.round(azar() * geometria.grosor.variacion);
    const tono = TONOS[Math.floor(azar() * TONOS.length)];
    const azarLlegada = azar();
    return { ...posicion, ...proyectar(posicion, largo), largo, grosor, tono, azarLlegada };
}

function huecosDelAnillo(
    geometria: GeometriaBiblioteca,
    azar: () => number,
    sector: number,
    anillo: number,
    primerIndice: number,
): RegistroBiblioteca[] {
    const radioAnillo = geometria.radios[anillo];
    const margen = ((geometria.separacion / radioAnillo) * 180) / Math.PI;
    const cantidad = geometria.huecosPorAnillo[anillo];
    const paso = (GRADOS_SECTOR - 2 * margen) / cantidad;
    return Array.from({ length: cantidad }, (_, j) =>
        crearRegistro(geometria, azar, {
            sector,
            anillo,
            indiceEnSector: primerIndice + j,
            angulo: 180 + GRADOS_SECTOR * sector + margen + paso * (j + 0.5),
            radio: radioAnillo + geometria.desfaseRadio,
        }),
    );
}

// Todos los huecos en el orden de dibujo del tablero (sector, anillo, posición).
function todosLosHuecos(geometria: GeometriaBiblioteca): RegistroBiblioteca[] {
    const azar = crearAzar(SEMILLA_BIBLIOTECA);
    const huecos: RegistroBiblioteca[] = [];
    for (let sector = 0; sector < SECTORES; sector++) {
        let indice = 0;
        geometria.radios.forEach((_, anillo) => {
            const delAnillo = huecosDelAnillo(geometria, azar, sector, anillo, indice);
            huecos.push(...delAnillo);
            indice += delAnillo.length;
        });
    }
    return huecos;
}

// Los registros que la biblioteca enseña: los huecos llenos de cada sector.
export function generarRegistros(geometria: GeometriaBiblioteca): RegistroBiblioteca[] {
    return todosLosHuecos(geometria).filter((hueco) => hueco.indiceEnSector < geometria.llenos[hueco.sector]);
}

// El primer hueco libre de cada sector con sitio: donde aterriza lo que el asistente guarda (Heroe_B2).
// El consumidor decide su tono; el que trae es el que sacó el azar.
export function huecosLibres(geometria: GeometriaBiblioteca): RegistroBiblioteca[] {
    return todosLosHuecos(geometria).filter((hueco) => hueco.indiceEnSector === geometria.llenos[hueco.sector]);
}

// Sector bajo un punto relativo al centro, con la fórmula de funcionEn() del tablero: empieza en
// 180° y el ángulo 360° exacto cae en el último sector, no en uno noveno.
export function sectorDeAngulo(dx: number, dy: number): number {
    const grados = ((((Math.atan2(dy, dx) * 180) / Math.PI - 180) % 360) + 360) % 360;
    return Math.min(SECTORES - 1, Math.floor(grados / GRADOS_SECTOR));
}
